import pipeDataConfigApi from '@/api/config/pipeDataConfigApi'
import hydraulicDataConfig from '@/api/config/hydraulicDataConfig'

interface FlatPipe {
  id?: unknown
  parentId?: unknown
}

/**
 * The pipes under `id`, deepest first, from a flat list that carries
 * `parentId` — the order they can be deleted in.
 *
 * SCRUM-1032 D2 retest: the API deletes only the pipe it is given. The
 * controller takes the sub-pipes with it, but their database rows stay, hidden
 * from the pipe list (which nests under live parents only) yet still offered
 * in the Station step's pipe dropdown, and re-attached to the next pipe that
 * is given the same id. So the sub-pipes are deleted first, explicitly.
 * Bounded: each pipe is visited once.
 */
export const subPipeDeleteOrder = (id: unknown, pipes: FlatPipe[]): string[] => {
  const childrenOf = new Map<string, string[]>()
  pipes.forEach((pipe) => {
    if (pipe.parentId === null || pipe.parentId === undefined) return
    const parent = String(pipe.parentId)
    childrenOf.set(parent, [...(childrenOf.get(parent) ?? []), String(pipe.id)])
  })
  const order: string[] = []
  const seen = new Set<string>([String(id)])
  const visit = (parent: string) => {
    for (const child of childrenOf.get(parent) ?? []) {
      if (seen.has(child)) continue
      seen.add(child)
      visit(child)
      order.push(child)
    }
  }
  visit(String(id))
  return order
}

/**
 * Deletes every sub-pipe under `id`, deepest first. Reads the flat pipe list
 * fresh, so sub-pipes on pages the grid has not loaded are included. Stops at
 * the first refusal (a sub-pipe still used by a station or a flow rule), which
 * the caller reports.
 */
export const deleteSubPipes = async (interfaceId: string, id: string) => {
  const { data } = await pipeDataConfigApi.getPipeDropDown(Number(interfaceId))
  const pipes: FlatPipe[] = Array.isArray(data) ? data : (data?.data ?? [])
  for (const childId of subPipeDeleteOrder(id, pipes)) {
    await hydraulicDataConfig.delete({ interfaceId, id: childId })
  }
}
